import type { CountingProgress, Phase } from '@val/shared'
import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchProgress } from '../lib/api.ts'
import { POLL_INTERVAL_MS } from '../lib/config.ts'

interface State {
  progress: CountingProgress | null
  error: string | null
  loading: boolean
}

/** Pollar räkningsstatus – används medan kommunens egen resultatfil saknas. */
export function useCountingProgress(phase?: Phase, poll = true) {
  const [state, setState] = useState<State>({ progress: null, error: null, loading: true })
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const alive = useRef(true)

  const load = useCallback(async () => {
    try {
      const progress = await fetchProgress(phase)
      if (!alive.current) return
      setState({ progress, error: null, loading: false })
    } catch (err) {
      if (!alive.current) return
      setState((s) => ({
        ...s,
        error: err instanceof Error ? err.message : 'Kunde inte hämta räkningsstatus',
        loading: false,
      }))
    }
  }, [phase])

  useEffect(() => {
    alive.current = true
    setState((s) => ({ ...s, loading: true }))
    load()

    if (poll) {
      const tick = () => {
        timer.current = setTimeout(async () => {
          if (document.visibilityState === 'visible') await load()
          tick()
        }, POLL_INTERVAL_MS)
      }
      tick()
    }

    return () => {
      alive.current = false
      if (timer.current) clearTimeout(timer.current)
    }
  }, [load, poll])

  return { ...state, reload: load }
}
